/**
 * DAS2 Declaration Controller (EPIC-023 / 23.5 — §12).
 *
 * Page : `/das2-declaration.html`. Vue préparation/soumission DAS2 :
 *   - chargement entête + bénéficiaires validés (GET …/declarations/{year},
 *     GET …/beneficiaries)
 *   - rendu tableau récap des bénéficiaires (status='validated' uniquement)
 *     via <template> + cloneTemplate + fillSlots
 *   - rendu sidebar Récap (total TTC, breakdown par catégorie)
 *   - téléchargements TXT / PDF : redirection navigateur directe
 *     (`window.location.href = downloadTxtUrl(year)`), le serveur émet
 *     `Content-Disposition: attachment`
 *
 * La logique de saisie d'accusé + POST submit est portée par
 * `das2-submission` (controller frère sur la même page).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

const CATEGORY_LABELS = {
  honoraires_pro_lib: "Honoraires",
  sous_traitance_indep: "Sous-traitance",
  commission_apporteur: "Commission",
  cession_droits_auteur: "Droits d'auteur",
};

export default class Das2DeclarationController extends BaseController {
  static targets = [
    "crumbYear",
    "title",
    "subtitle",
    "deadline",
    "beneficiaryCount",
    "beneficiaryTbody",
    "totalRow",
    "totalCount",
    "totalTtc",
    "declarantName",
    "declarantSiret",
    "declarantAddress",
    "declarantFiscalYear",
    "recapTotalTtc",
    "recapBeneficiaryCount",
    "recapYear",
    "recapCatHonoraires",
    "recapCatSousTraitance",
    "recapCatCommission",
    "recapCatDroitsAuteur",
    "downloadTxtLink",
    "downloadPdfLink",
    "actionsHint",
  ];

  static values = {
    year: { type: Number, default: 0 },
  };

  connect() {
    const yearFromQuery = this._readYearFromQuery();
    this._currentYear = yearFromQuery || this.yearValue || (new Date().getFullYear() - 1);
    this._renderYear(this._currentYear);
    this.loadDeclaration();
  }

  async loadDeclaration() {
    try {
      const data = await api.das2.getDeclaration(this._currentYear);
      this._renderHead(data);
      this._renderDeclarant(data);
      await this.loadBeneficiaries();
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        this.showError(`Aucune déclaration DAS2 trouvée pour l'exercice ${this._currentYear}.`);
        return;
      }
      this.showError(error instanceof APIError ? error.message : "Impossible de charger la déclaration.");
    }
  }

  async loadBeneficiaries() {
    try {
      const data = await api.das2.listBeneficiaries(this._currentYear);
      const list = Array.isArray(data) ? data : (data?.data ?? []);
      const validated = list.filter((b) => b.status === "validated");
      this._renderRows(validated);
      this._renderRecap(list);
      this._renderActionsHint(list);
    } catch (error) {
      this.showError(error instanceof APIError ? error.message : "Impossible de charger les bénéficiaires.");
    }
  }

  downloadTxt(event) {
    if (event) event.preventDefault();
    window.location.href = api.das2.downloadTxtUrl(this._currentYear);
  }

  downloadPdf(event) {
    if (event) event.preventDefault();
    window.location.href = api.das2.downloadPdfUrl(this._currentYear);
  }

  _readYearFromQuery() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("year");
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  _renderYear(year) {
    if (this.hasCrumbYearTarget) this.crumbYearTarget.textContent = String(year);
    if (this.hasTitleTarget) this.titleTarget.textContent = String(year);
    if (this.hasRecapYearTarget) this.recapYearTarget.textContent = String(year);
    if (this.hasDeadlineTarget) this.deadlineTarget.textContent = `5 mai ${year + 1}`;
  }

  _renderHead(data) {
    const year = data?.fiscalYear ?? this._currentYear;
    this._currentYear = year;
    this._renderYear(year);
    if (this.hasDeadlineTarget && data?.deadlineDate) {
      this.deadlineTarget.textContent = this.formatShortDate(data.deadlineDate);
    }
  }

  _renderDeclarant(data) {
    const declarant = data?.declarant ?? {};
    if (this.hasDeclarantNameTarget) {
      this.declarantNameTarget.value = declarant.name ?? "";
    }
    if (this.hasDeclarantSiretTarget) {
      this.declarantSiretTarget.value = this._formatSiret(declarant.siret ?? "");
    }
    if (this.hasDeclarantAddressTarget) {
      const parts = [declarant.address, declarant.postalCode, declarant.city].filter(Boolean);
      this.declarantAddressTarget.value = parts.join(", ");
    }
    if (this.hasDeclarantFiscalYearTarget) {
      const year = data?.fiscalYear ?? this._currentYear;
      this.declarantFiscalYearTarget.value = `01/01/${year} → 31/12/${year}`;
    }
  }

  _renderRows(validatedList) {
    if (!this.hasBeneficiaryTbodyTarget) return;
    this.beneficiaryTbodyTarget.replaceChildren();

    validatedList.forEach((b) => {
      const fragment = this.cloneTemplate("das2-declaration-beneficiary-row-template");
      const overrides = b.overrides ?? {};
      const siret = overrides.siret ?? b.siret ?? "—";
      const addressLine1 = overrides.address ?? b.address ?? "";
      const postalCode = overrides.postalCode ?? b.postalCode ?? "";
      const city = overrides.city ?? b.city ?? "";
      const addressLine2 = [postalCode, city].filter(Boolean).join(" ");

      this.fillSlots(fragment, {
        supplierName: b.supplierName ?? "—",
        supplierMeta: this._buildSupplierMeta(b),
        siret: this._formatSiret(siret),
        category: CATEGORY_LABELS[b.category] ?? b.category ?? "—",
        amountTtc: `${this._formatAmount(Number(b.computedTotalTtc ?? 0))} €`,
      });

      const addressCell = fragment.querySelector('[data-slot="address"]');
      if (addressCell) {
        addressCell.replaceChildren();
        if (addressLine1) addressCell.append(document.createTextNode(addressLine1));
        if (addressLine1 && addressLine2) addressCell.append(document.createElement("br"));
        if (addressLine2) addressCell.append(document.createTextNode(addressLine2));
      }

      this.beneficiaryTbodyTarget.append(fragment);
    });
  }

  _renderRecap(allBeneficiaries) {
    const validated = allBeneficiaries.filter((b) => b.status === "validated");

    if (this.hasBeneficiaryCountTarget) {
      this.beneficiaryCountTarget.textContent = String(validated.length);
    }
    if (this.hasTotalCountTarget) {
      this.totalCountTarget.textContent = String(validated.length);
    }
    if (this.hasRecapBeneficiaryCountTarget) {
      this.recapBeneficiaryCountTarget.textContent = String(validated.length);
    }

    const totalTtc = validated.reduce((sum, b) => sum + Number(b.computedTotalTtc ?? 0), 0);
    if (this.hasTotalTtcTarget) {
      this.totalTtcTarget.textContent = `${this._formatAmount(totalTtc)} €`;
    }
    if (this.hasRecapTotalTtcTarget) {
      this.recapTotalTtcTarget.textContent = this._formatAmount(totalTtc, 0);
    }

    const sums = { honoraires_pro_lib: 0, sous_traitance_indep: 0, commission_apporteur: 0, cession_droits_auteur: 0 };
    validated.forEach((b) => {
      const cat = b.category;
      if (cat in sums) sums[cat] += Number(b.computedTotalTtc ?? 0);
    });

    const setCat = (target, value) => {
      if (!target) return;
      target.textContent = value > 0 ? `${this._formatAmount(value)} €` : "— € (à valider)";
    };
    setCat(this.hasRecapCatHonorairesTarget ? this.recapCatHonorairesTarget : null, sums.honoraires_pro_lib);
    setCat(this.hasRecapCatSousTraitanceTarget ? this.recapCatSousTraitanceTarget : null, sums.sous_traitance_indep);
    setCat(this.hasRecapCatCommissionTarget ? this.recapCatCommissionTarget : null, sums.commission_apporteur);
    setCat(this.hasRecapCatDroitsAuteurTarget ? this.recapCatDroitsAuteurTarget : null, sums.cession_droits_auteur);
  }

  _renderActionsHint(allBeneficiaries) {
    if (!this.hasActionsHintTarget) return;
    const detected = allBeneficiaries.length;
    const validated = allBeneficiaries.filter((b) => b.status === "validated").length;
    const pending = allBeneficiaries.filter((b) => b.status === "pending").length;
    if (pending > 0) {
      this.actionsHintTarget.textContent =
        `— ${validated} bénéficiaire(s) validé(s) sur ${detected} détecté(s) · pensez à compléter les ${pending} ligne(s) restante(s)`;
    } else {
      this.actionsHintTarget.textContent =
        `— ${validated} bénéficiaire(s) prêt(s) pour la déclaration · vous pouvez télécharger le fichier DAS2`;
    }
  }

  _buildSupplierMeta(beneficiary) {
    const overrides = beneficiary.overrides ?? {};
    const legalForm = overrides.legalForm ?? beneficiary.legalForm ?? "";
    const category = CATEGORY_LABELS[beneficiary.category] ?? "";
    return [category, legalForm].filter(Boolean).join(" — ");
  }

  _formatSiret(raw) {
    if (!raw) return "—";
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length !== 14) return String(raw);
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }

  _formatAmount(amount, decimals = 2) {
    return amount.toLocaleString("fr-FR", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
}
